import { useRef, useState } from "react";
import { Camera, FileText, Loader2, Lock, Paperclip, ShieldAlert, X } from "lucide-react";
import api from "../../lib/api";
import { CLASSIFICATIONS, FILE_ACCEPT, FILES_MAX, STATE_LABELS, fileProblem, newKey, recordsError, stateTone } from "../../lib/records";
import useIsMobile from "../../hooks/useIsMobile";
import { ReceiptPreviewDialog } from "../finance/ReceiptPreview";
import { Button } from "../ui/button";

export function StateBadge({ state }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${stateTone(state)}`}
      data-testid={`records-state-${state}`}>
      {STATE_LABELS[state] || state}
    </span>
  );
}

export function ClassificationBadge({ value }) {
  if (!value || value === "internal") return null;
  const Icon = value === "restricted" ? Lock : ShieldAlert;
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-600 whitespace-nowrap"
      data-testid={`records-classification-${value}`}>
      <Icon size={12} aria-hidden="true" />{CLASSIFICATIONS[value].label}
    </span>
  );
}

export function Field({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-3 py-1.5">
      <dt className="text-xs sm:text-sm text-slate-500 sm:w-36 shrink-0">{label}</dt>
      <dd className="text-sm text-slate-900 min-w-0 break-words">{children}</dd>
    </div>
  );
}

// Files of a version: each opens a preview fetched through the private files route (the same access check and journal entry as a
// download — never a storage address).
export function FileLinks({ files }) {
  const [open, setOpen] = useState(null);
  if (!files?.length) return <span className="text-sm text-slate-500">Aucun fichier</span>;
  return (
    <>
      <ul className="flex flex-wrap gap-2">
        {files.map((f) => (
          <li key={f.id}>
            <button type="button" onClick={() => setOpen({ source: { key: `file:${f.id}`, path: `/files/${f.id}` }, name: f.name })}
              className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
              data-testid="records-file-link">
              <FileText size={13} aria-hidden="true" /><span className="truncate">{f.name}</span>
            </button>
          </li>
        ))}
      </ul>
      <ReceiptPreviewDialog receipt={open} onClose={() => setOpen(null)} description="Aperçu du document. Chaque ouverture est journalisée." />
    </>
  );
}

// The files of a new version (or of a received document, or of the signed copy): sent at once through the private files route of
// this module, kept as ids until the form is saved. A file removed here stays unattached and is cleaned up like any abandoned upload.
export function RecordFilesField({ files, setFiles, required = false, label = "Fichier", disabled, testid = "records-files" }) {
  const isMobile = useIsMobile();
  const [uploading, setUploading] = useState(false);
  const [problems, setProblems] = useState([]);
  const picker = useRef(null);
  const camera = useRef(null);

  const send = async (picked, input) => {
    if (input) input.value = "";
    const refused = [];
    const kept = [];
    for (const file of picked) {
      const problem = fileProblem(file);
      if (problem) refused.push(problem);
      else if (files.length + kept.length >= FILES_MAX) refused.push(`${FILES_MAX} fichiers au plus : ${file.name} n'a pas été ajouté.`);
      else kept.push(file);
    }
    setProblems(refused);
    if (!kept.length) return;
    setUploading(true);
    try {
      for (const file of kept) {
        const form = new FormData();
        form.append("file", file);
        try {
          const { data } = await api.post("/files", form, { params: { context: "records_document" }, headers: { "Idempotency-Key": newKey() } });
          setFiles((list) => [...list, { id: data.id, name: data.name || file.name }]);
        } catch (e) {
          setProblems((list) => [...list, `${file.name} : ${recordsError(e, "fichier refusé.")}`]);
        }
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="text-sm font-medium text-slate-900">
        {label} <span className={required ? "text-amber-700 text-xs font-normal" : "text-slate-500 text-xs font-normal"}>{required ? "· obligatoire" : "· facultatif"}</span>
      </div>
      <p className="text-xs text-slate-500">PDF ou image (JPEG, PNG, WebP), 10 Mo maximum par fichier.</p>
      {files.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label="Fichiers joints">
          {files.map((f) => (
            <li key={f.id} className="flex max-w-full items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 py-1 pl-2 pr-1 text-xs" data-testid="records-file">
              <FileText size={13} aria-hidden="true" className="shrink-0 text-slate-400" /><span className="min-w-0 truncate">{f.name}</span>
              <button type="button" onClick={() => setFiles((list) => list.filter((x) => x.id !== f.id))} disabled={disabled} aria-label={`Retirer ${f.name}`}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-slate-200"><X size={14} /></button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => picker.current?.click()} disabled={disabled || uploading || files.length >= FILES_MAX}
          data-testid={`${testid}-add`}>
          {uploading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Paperclip size={14} className="mr-1.5" />}
          {uploading ? "Envoi…" : files.length ? "Joindre un autre fichier" : "Joindre"}
        </Button>
        {isMobile && (
          <Button type="button" variant="outline" size="sm" onClick={() => camera.current?.click()} disabled={disabled || uploading}>
            <Camera size={14} className="mr-1.5" />Prendre une photo
          </Button>
        )}
        <input ref={picker} type="file" multiple className="hidden" accept={FILE_ACCEPT.join(",")} aria-label={label} data-testid={`${testid}-input`}
          onChange={(e) => send(Array.from(e.target.files || []), e.target)} />
        <input ref={camera} type="file" className="hidden" accept="image/jpeg,image/png,image/webp" capture="environment" tabIndex={-1} aria-hidden="true"
          onChange={(e) => send(Array.from(e.target.files || []), e.target)} />
      </div>
      {problems.length > 0 && <ul role="alert" className="space-y-0.5 text-xs text-red-700">{problems.map((p) => <li key={p}>{p}</li>)}</ul>}
    </div>
  );
}
